import type { IconType } from "react-icons";
import { FaCheck, FaRegComment } from "react-icons/fa";
import {
  AwardCriterionType,
  ContractNature,
  EspdCriterionCategory,
  FoaPart,
  FrameworkAgreementType,
  MinimumRequirementResponseType,
  ProcedureType,
  ReservedParticipationType,
  ReservedExecutionType,
  SelectionCriterionType,
} from "../../../../../autogen/bff-api";

// Each OPTIONS array drops the redundant `labelKey` field — consumers call
// `t(option.value)` directly, with SCREAMING_SNAKE_CASE keys living in
// `webapp/locales/{en,no}/translation.json`. Description text remains
// phrase-keyed because it does not map cleanly onto enum values.
//
// Enum variants are pinned for langen by `src/autogen/EnumKeys.tsx`
// (regenerated from `bff-api.ts` by `codegen.ts`). To make a new BFF enum
// translatable, add it to `TRANSLATED_ENUMS` in `codegen.ts`.

// ── Framework Agreement ──────────────────────────────────────────────

// t('Ordering directly from the agreement without new competition')
// t('New competition among the suppliers on the framework agreement')
// t('Both direct call-off and mini-competition, depending on value')
export const FRAMEWORK_AGREEMENT_OPTIONS = [
  {
    value: FrameworkAgreementType.WithoutReopening,
    descriptionKey: "Ordering directly from the agreement without new competition",
  },
  {
    value: FrameworkAgreementType.WithReopening,
    descriptionKey: "New competition among the suppliers on the framework agreement",
  },
  {
    value: FrameworkAgreementType.Mixed,
    descriptionKey: "Both direct call-off and mini-competition, depending on value",
  },
] as const;

export const isFrameworkEnabled = (value: FrameworkAgreementType | null | undefined): boolean =>
  value != null && value !== FrameworkAgreementType.None;

// ── Contract Nature ──────────────────────────────────────────────────

// t('Purchase of goods or products') t('Purchase of services') t('Building and construction work')
export const CONTRACT_NATURE_OPTIONS = [
  { value: ContractNature.Supplies, descriptionKey: "Purchase of goods or products" },
  { value: ContractNature.Services, descriptionKey: "Purchase of services" },
  { value: ContractNature.Works, descriptionKey: "Building and construction work" },
] as const;

// ── Procedure Type ───────────────────────────────────────────────────

// t('All qualified suppliers can submit bids') t('Pre-qualification before bid submission')
// t('Negotiation with selected suppliers') t('Dialogue phase before final bids')
export const PROCEDURE_TYPE_OPTIONS = [
  { value: ProcedureType.Open, descriptionKey: "All qualified suppliers can submit bids", enabled: true },
  { value: ProcedureType.Restricted, descriptionKey: "Pre-qualification before bid submission", enabled: true },
  { value: ProcedureType.CompetitiveNegotiation, descriptionKey: "Negotiation with selected suppliers", enabled: false },
  { value: ProcedureType.CompetitiveDialogue, descriptionKey: "Dialogue phase before final bids", enabled: false },
] as const;

// ── Reserved Participation ──────────────────────────────────────────

// t('No restriction on participation') t('Reserved for sheltered workshops and similar enterprises')
// t('Reserved for organizations with a public service mission')
export const RESERVED_PARTICIPATION_OPTIONS = [
  { value: ReservedParticipationType.None, descriptionKey: "No restriction on participation" },
  { value: ReservedParticipationType.ShelteredWorkshop, descriptionKey: "Reserved for sheltered workshops and similar enterprises" },
  { value: ReservedParticipationType.PublicServiceMission, descriptionKey: "Reserved for organizations with a public service mission" },
] as const;

// ── Reserved Execution ──────────────────────────────────────────────

// t('No restriction on execution') t('Execution reserved for sheltered employment programmes')
// t('Reservation status not yet determined')
export const RESERVED_EXECUTION_OPTIONS = [
  { value: ReservedExecutionType.None, descriptionKey: "No restriction on execution" },
  { value: ReservedExecutionType.ShelteredEmployment, descriptionKey: "Execution reserved for sheltered employment programmes" },
  { value: ReservedExecutionType.NotKnown, descriptionKey: "Reservation status not yet determined" },
] as const;

// ── Selection Criterion Type ────────────────────────────────────────
//
// "Selection criteria" (EU-direktiv 2014/24/EU) === "kvalifikasjonskrav" (FOA).
// The DTO field is `selectionCriteria`; the UI says "Kvalifikasjonskrav".
// See docs/adr/002-qualification-requirements.md (Decision 1).
export const SELECTION_CRITERION_TYPE_OPTIONS = [
  { value: SelectionCriterionType.Suitability, badgeBg: "smSecondarySoft", badgeColor: "smSecondary" },
  { value: SelectionCriterionType.EconomicFinancial, badgeBg: "smQuaternarySoft", badgeColor: "smQuaternary" },
  { value: SelectionCriterionType.TechnicalProfessional, badgeBg: "smPrimarySofter", badgeColor: "smPrimary" },
  { value: SelectionCriterionType.QualityEnvironment, badgeBg: "smTertiarySoft", badgeColor: "smTertiary" },
  { value: SelectionCriterionType.Other, badgeBg: "smBackgroundSecondary", badgeColor: "smMuted" },
] as const;

// ── Minimum Requirement Response Type ───────────────────────────────
//
// "Minstekrav" — the answer format a supplier must give. Single source for the drawer cards
// (labelKey + descriptionKey + hintKey) and the list/summary badge (icon + labelKey + colors).
export const MINIMUM_REQUIREMENT_RESPONSE_TYPE_OPTIONS: {
  value: MinimumRequirementResponseType;
  labelKey: string;
  descriptionKey: string;
  hintKey: string | null;
  icon: IconType;
  badgeBg: string;
  badgeColor: string;
}[] = [
  {
    value: MinimumRequirementResponseType.YesNo,
    labelKey: "Yes / No",
    descriptionKey: "A simple yes or no answer.",
    hintKey: null,
    icon: FaCheck,
    badgeBg: "smStatusOkBg",
    badgeColor: "smStatusOkText",
  },
  {
    value: MinimumRequirementResponseType.YesNoComment,
    labelKey: "Yes / No + comment",
    descriptionKey: "Yes or no, with room for an explanatory comment.",
    hintKey: "Respondent gets a comment field",
    icon: FaRegComment,
    badgeBg: "smStatusOkBg",
    badgeColor: "smStatusOkText",
  },
];

// ── Award Criterion Model ───────────────────────────────────────────
//
// Note: values are BFF DTO discriminator fields (`eventData.awardCriteria.lowestPrice`),
// not enum members — so they keep phrase-keyed labels.

// t('Lowest price') t('Award based on lowest price alone')
// t('Best price-quality ratio') t('Award based on weighted criteria including quality')
export const AWARD_CRITERION_MODEL_OPTIONS = [
  {
    value: "lowestPrice" as const,
    labelKey: "Lowest price",
    descriptionKey: "Award based on lowest price alone",
  },
  {
    value: "bestPriceQualityRatio" as const,
    labelKey: "Best price-quality ratio",
    descriptionKey: "Award based on weighted criteria including quality",
  },
] as const;

export type AwardCriterionModel = (typeof AWARD_CRITERION_MODEL_OPTIONS)[number]["value"];

// ── Award Criterion Type (per criterion) ────────────────────────────

// The environment sub-types collapse into one "Miljø" choice in the type picker. The buyer never picks
// criteria/spec manually — the BFF derives which of those two is stored (and thus which Doffin
// award-criterion-type code eForms emits) from the total environment weighting. The three § 5c
// exemptions are explicit "no environmental weighting" choices (never weight-derived), chosen via the
// exemption control and rendered as the § 5b/§ 5c justification box rather than a criterion card.
export const ENVIRONMENT_GROUP_VALUE = "ENVIRONMENT";

export const AWARD_CRITERION_TYPE_OPTIONS: { value: string; labelKey: string }[] = [
  { value: AwardCriterionType.Price, labelKey: AwardCriterionType.Price },
  { value: AwardCriterionType.Cost, labelKey: AwardCriterionType.Cost },
  { value: AwardCriterionType.Quality, labelKey: AwardCriterionType.Quality },
  { value: ENVIRONMENT_GROUP_VALUE, labelKey: AwardCriterionType.QualityEnvironmentalCriteria },
];

// Award-criterion types that count as climate/environment weighting under § 5b (the "green" paths
// A/B) — mirrors the BFF weighting set and drives the green badge colour.
export const ENVIRONMENTAL_AWARD_CRITERION_TYPES: readonly AwardCriterionType[] = [
  AwardCriterionType.QualityEnvironmentalCriteria,
  AwardCriterionType.QualityEnvironmentalSpecification,
];

// The three § 5c exemptions, mutually exclusive. Each is an explicit "no environmental weighting"
// choice stored as a single marker criterion (never weight-derived). They replaced the single
// pre-2026 exemption when FOA § 7-9 was superseded on 2026-07-01.
export const ENVIRONMENT_EXEMPTION_TYPES: readonly AwardCriterionType[] = [
  AwardCriterionType.QualityEnvironmentalExemptInsignificant,
  AwardCriterionType.QualityEnvironmentalExemptCriticalInterest,
  AwardCriterionType.QualityEnvironmentalExemptNationalMinimum,
];

// Every sub-type an environment criterion can take, including the exemption markers — used to
// identify environment rows regardless of sub-type.
export const ENVIRONMENT_CRITERION_TYPES: readonly AwardCriterionType[] = [
  ...ENVIRONMENTAL_AWARD_CRITERION_TYPES,
  ...ENVIRONMENT_EXEMPTION_TYPES,
];

export const isEnvironmentCriterionType = (type: AwardCriterionType): boolean =>
  ENVIRONMENT_CRITERION_TYPES.includes(type);

// A § 5c exemption is stored as a single exemption marker criterion (one of three). It is rendered as
// the justification box (never a criterion card) and filtered out of the weights table.
export const isExemptionMarker = (type: AwardCriterionType): boolean =>
  ENVIRONMENT_EXEMPTION_TYPES.includes(type);

// The active § 5c exemption type in a criteria list, if any. The BFF keeps at most one marker, so
// this returns the first — used to drive the exemption-reason selector and its template.
export const activeExemptionType = (
  criteria: readonly { type: AwardCriterionType }[],
): AwardCriterionType | null => criteria.find((c) => isExemptionMarker(c.type))?.type ?? null;

// Whether the buyer has chosen a § 5c exemption: the criteria list holds an exemption marker AND no
// weighted environment criterion. A real environment criterion wins over the marker (the server drops
// the marker), so adding one is enough to leave the exemption — mirror that here so the UI doesn't
// flash the exemption box while an environment row is being added.
export const isExemption = (criteria: readonly { type: AwardCriterionType }[]): boolean =>
  criteria.some((c) => isExemptionMarker(c.type)) &&
  !criteria.some((c) => isEnvironmentCriterionType(c.type) && !isExemptionMarker(c.type));

// Collapse any environment sub-type to the single "Miljø" dropdown value.
export const criterionTypeSelectValue = (type: AwardCriterionType): string =>
  isEnvironmentCriterionType(type) ? ENVIRONMENT_GROUP_VALUE : type;

// Display label key for a criterion's type in the summary. The enum value is its own translation key
// (see the autogen EnumKeys / locales), so each environment sub-type and exemption keeps a distinct label.
export const criterionTypeLabelKey = (type: AwardCriterionType): string => type;

// anskaffelsesloven § 5b: minimum climate/environment weighting before a justification is required.
// NOTE: the BFF is the source of truth for this rule — `AwardCriteria.normalized()` re-derives every
// criterion's sub-type and the gate enforces the 30 % floor server-side on every write. This client
// copy (threshold + the type sets) exists only for instant UX (live dropdown,
// badge, prefilled box) and cannot be shared across the Kotlin/TypeScript boundary; keep it in sync.
export const MIN_ENVIRONMENT_WEIGHTING_PERCENT = 30;

// The climate/environment award rule (anskaffelsesloven § 5b/§ 5c) applies only to Del II/III procurements.
export const environmentRuleApplies = (foaPart: FoaPart | null | undefined): boolean =>
  foaPart === FoaPart.PartIi || foaPart === FoaPart.PartIii;

// NOTE: the criteria-vs-specification sub-type (vei A vs vei B) is derived by the BFF from the SUMMED
// environment weighting (the 30 % floor is on the total, so two 15 % criteria together are vei A) — see
// `AwardCriteria.normalized()`. The client never classifies it per criterion; it just tags the row as
// the environment family and lets the BFF (source of truth) settle criteria vs spec on each write.

// ── Exclusion Grounds Category ──────────────────────────────────────
//
// Categories A-G from EHF ESPD 1.2 used to group ESPD Part III (avvisningsgrunner).
// Only the EXCLUSION_* variants of EspdCriterionCategory are reachable here; the
// SELECTION_* and OTHER_* variants are filtered out by the BFF taxonomy endpoint.
// See docs/adr/005-exclusion-grounds.md.
export const EXCLUSION_GROUNDS_CATEGORY_OPTIONS = [
  { value: EspdCriterionCategory.ExclusionConvictions },
  { value: EspdCriterionCategory.ExclusionContributions },
  { value: EspdCriterionCategory.ExclusionSocial },
  { value: EspdCriterionCategory.ExclusionBusiness },
  { value: EspdCriterionCategory.ExclusionMisconduct },
  { value: EspdCriterionCategory.ExclusionConflict },
  { value: EspdCriterionCategory.ExclusionNational },
] as const;
